if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('[id^="GalleryDots"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        this.updateArrowTheme();

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('.product-gallery__dot').forEach((dot) => {
            dot.addEventListener('click', this.onDotClick.bind(this));
          });
        }
      }

      onSlideChanged(event) {
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
          );
          this.setActiveThumbnail(thumbnail);
        }
        if (this.elements.dots) {
          const visibleSlides = this.getVisibleSlides();
          const activeIndex = visibleSlides.indexOf(event.detail.currentElement);
          this.updateDots(activeIndex >= 0 ? activeIndex + 1 : event.detail.currentPage);
        }
        this.updateArrowTheme(event.detail.currentElement);
      }

      onDotClick(event) {
        const index = parseInt(event.currentTarget.dataset.index, 10) - 1;
        const slider = this.elements.viewer.querySelector('[id^="Slider-"]');
        const visibleSlides = this.getVisibleSlides();
        if (visibleSlides[index] && slider) {
          slider.scrollTo({
            left: visibleSlides[index].offsetLeft,
            behavior: 'smooth',
          });
          this.updateArrowTheme(visibleSlides[index]);
          this.updateDots(index + 1);
        }
      }

      updateDots(currentPage) {
        if (!this.elements.dots) return;
        const dots = this.elements.dots.querySelectorAll('.product-gallery__dot');
        if (!dots.length) return;
        const page = Math.min(Math.max(currentPage, 1), dots.length);
        dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === page - 1);
        });
      }

      normalizeAltText(alt) {
        return (alt || '')
          .trim()
          .toLowerCase()
          .replace(/[\u2013\u2014]/g, '-')
          .replace(/\s+/g, ' ');
      }

      getGallerySlides() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]:not([id^="Slide-Thumbnails"])'));
      }

      getVisibleSlides() {
        return this.getGallerySlides().filter((slide) => !slide.classList.contains('vocci-gallery-slide--hidden'));
      }

      rebuildDots(visibleSlides) {
        if (!this.elements.dots) return;

        const container = this.elements.dots;
        container.innerHTML = '';

        visibleSlides.forEach((_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-gallery__dot' + (index === 0 ? ' is-active' : '');
          dot.dataset.index = String(index + 1);
          dot.setAttribute('aria-label', `Slide ${index + 1}`);
          dot.addEventListener('click', this.onDotClick.bind(this));
          container.appendChild(dot);
        });

        container.style.display = visibleSlides.length <= 1 ? 'none' : '';
      }

      setActiveMedia(mediaId, prepend) {
        // If a color filter is pending (color swatch just changed), filterByColor will
        // handle the active state and scroll — skip here to prevent a visible flash.
        if (this._pendingColorFilter) return;

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        this.updateArrowTheme(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!activeMedia.classList.contains('is-active')) return;
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails && !this.elements.dots) return;

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        if (this.elements.dots) {
          const visibleSlides = this.getVisibleSlides();
          const activeIndex = visibleSlides.indexOf(activeMedia);
          if (activeIndex >= 0) {
            this.updateDots(activeIndex + 1);
          }
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      updateArrowTheme(activeSlide) {
        if (!activeSlide) {
          activeSlide =
            this.querySelector('.product-gallery__item.is-active') || this.querySelector('.product-gallery__item');
        }
        if (!activeSlide) return;

        const alt = (activeSlide.dataset.mediaAlt || '').toLowerCase();
        const isBlack = alt.includes('black');

        this.querySelectorAll('.product-gallery__arrow').forEach((arrow) => {
          arrow.classList.toggle('is-dark', isBlack);
        });

        if (this.elements.dots) {
          this.elements.dots.classList.toggle('product-gallery__dots--dark', isBlack);
        }
      }

      /**
       * Filter gallery slides by selected color value.
       * Shows slides whose alt contains `prefix + colorValue` (case-insensitive),
       * plus any slides whose alt does NOT contain the prefix at all (shared images).
       * Color-specific slides are ordered before shared images.
       *
       * @param {string} colorValue - The selected color value.
       * @param {string} prefix - The alt-text prefix used to identify color-specific slides.
       * @param {string} [preferredMediaId] - When provided, this media id (data-media-id) is
       *   pinned to the FIRST position among color slides and marked as the active slide.
       *   Used so that clicking a color immediately surfaces that variant's featured image,
       *   rather than whichever color-matching slide happens to be first in DOM order.
       */
      filterByColor(colorValue, prefix) {
        const viewer = this.elements.viewer;
        if (!viewer || !colorValue || !prefix) return;

        const slides = this.getGallerySlides();
        const prefixNormalized = this.normalizeAltText(prefix);
        const targetNormalized = this.normalizeAltText(prefix + colorValue);
        const colorNormalized = this.normalizeAltText(colorValue);
        const visibleSlides = [];

        slides.forEach((slide) => {
          const alt = this.normalizeAltText(slide.dataset.mediaAlt);
          const isColorSpecific = alt.includes(prefixNormalized);
          let show = true;

          if (isColorSpecific) {
            const colorSuffix = alt.slice(alt.indexOf(prefixNormalized) + prefixNormalized.length).trim();
            show =
              colorSuffix === colorNormalized ||
              colorSuffix.includes(colorNormalized) ||
              alt.includes(targetNormalized);
          }

          slide.classList.remove('is-active', 'vocci-gallery-slide--hidden');
          slide.style.display = '';

          if (!show) {
            slide.classList.add('vocci-gallery-slide--hidden');
            slide.style.display = 'none';
          } else {
            visibleSlides.push(slide);
          }
        });

        // 不对 DOM 顺序做任何改动，直接显示第一个可见图
        const firstVisible = visibleSlides[0];
        if (firstVisible) {
          firstVisible.classList.add('is-active');
          const slider = viewer.querySelector('[id^="Slider-"]');
          if (slider) slider.scrollTo({ left: 0 });
          this.updateArrowTheme(firstVisible);
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((item) => {
            const target = viewer.querySelector(`[data-media-id="${item.dataset.target}"]`);
            const hidden = !target || target.classList.contains('vocci-gallery-slide--hidden');
            item.style.display = hidden ? 'none' : '';
          });
        }

        this.rebuildDots(visibleSlides);

        // Color filter is now fully applied — allow setActiveMedia to work again.
        this._pendingColorFilter = false;

        if (typeof viewer.resetPages === 'function') {
          requestAnimationFrame(() => viewer.resetPages());
        }
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    },
  );
}
